import { AuthApi, IS_ESOFT_USER_KEY } from 'api';
import appRoutes from 'app.routes';
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QUERIES from 'modules/common/queries';
import { forgetNewOrderState } from 'modules/orders/context';
import { useAppEvents } from 'modules/common/app.events';
import { AUTH_REDIRECT_URL, MINDWORKING_REDIRECT_REFERENCE } from 'api/http';
import { useResetPersistedFilter } from 'modules/common/hooks';
import qs from 'qs';
import { userStore } from 'stores/userStore';

const BLACK_LISTED_REDIRECT_URLS = [appRoutes.activeDirectory(), appRoutes.login(), appRoutes.authRedirect()];

export function useAuth() {
  const client = useQueryClient();

  const appEvents = useAppEvents();
  const resetPersistedFilters = useResetPersistedFilter();
  const { provider } =
    typeof window !== 'undefined'
      ? (qs.parse(window.location.search.substring(1)) as Partial<Record<string, string>>)
      : ({} as Partial<Record<string, string>>);

  const persistRedirectUrl = useCallback(() => {
    if (!BLACK_LISTED_REDIRECT_URLS.some(url => window.location.href.includes(url)) && !provider) {
      localStorage.setItem(AUTH_REDIRECT_URL, `${window.location.pathname}${window.location.search}`);
    }
  }, [provider]);

  const logout = useCallback(
    (userInitiatedLogout = false) => {
      // ignore logouts when forced by mindworking urls
      if (provider?.toLowerCase() === 'mindworking') return;

      userInitiatedLogout ? localStorage.removeItem(AUTH_REDIRECT_URL) : persistRedirectUrl();
      resetPersistedFilters();
      sessionStorage.clear();
      appEvents.dispatch('orders.new.context.clear');
      AuthApi.deauthenticate();
    },
    [persistRedirectUrl, appEvents, resetPersistedFilters, provider]
  );

  const {
    data: user,
    isLoading: userIsLoading,
    isFetched: userIsFetched,
    isError,
    refetch,
  } = useQuery({
    queryKey: [QUERIES.user],
    queryFn: async () => {
      return AuthApi.getAuthenticatedUser();
    },
    enabled:
      AuthApi.isAuthenticated() &&
      window.location.pathname !== appRoutes.activeDirectory() &&
      window.location.pathname !== appRoutes.authRedirect(), // prevent getting initial user on AD page which leads to 404 response for internal users
  });

  const softLogout = useCallback(async () => {
    forgetNewOrderState();
    if (localStorage.getItem(IS_ESOFT_USER_KEY) === 'true') {
      resetPersistedFilters();
      sessionStorage.clear();
      appEvents.dispatch('orders.new.context.clear');
      window.location.href = appRoutes.activeDirectory();
      client.removeQueries();
      AuthApi.clearAuthHeaders();
    } else {
      logout(true);
    }
  }, [client, logout, appEvents, resetPersistedFilters]);

  return {
    user,
    userIsLoading,
    userIsFetched,
    isError,
    logout,
    softLogout,
    persistRedirectUrl,
    refetch,
  };
}

export function useMindworkingUrl() {
  const queryClient = useQueryClient();
  const { maeglersMail, provider, sagsnr } =
    typeof window !== 'undefined'
      ? (qs.parse(window.location.search.substring(1)) as Partial<Record<string, string>>)
      : ({} as Record<string, string>);

  const isMindworking = provider?.toLowerCase() === 'mindworking';

  const query = useQuery({
    queryKey: [QUERIES.mindworking, maeglersMail],
    queryFn: async () => (maeglersMail ? await AuthApi.b2cAuth(maeglersMail, appRoutes.authRedirect()) : undefined),
    enabled: !!maeglersMail && !!sagsnr && isMindworking,
  });

  return {
    ...query,
    isMindworking,
  };
}
